/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 */

import View from "App/view/view";
import {mat4, vec3} from "gl-matrix";
import CameraVR from "App/view/cameras/camera_vr";
import {CameraTypeEnum} from "js_interface/src/modules/view_controls/scene_view_controls_enums";

/**
 *  Camera for Stereo view mode.
 **/
export default
class CameraStereo extends CameraVR {

    private DTR: number = 1.0;  // 0.0174532925;

    constructor(aView: View) {
        super(aView);
    }

    public getCameraType(): CameraTypeEnum {
        return CameraTypeEnum.STEREO;
    }

    // TODO: add support for the stereo projection matrix?
    public computeProjectionMatrixWithFarAtInfinity(): mat4 {
        // the matrix has the indexed organized to rows/columns like that:
        // 0    4   8  12
        // 1    5   9  13
        // 2    6  10  14
        // 3    7  11  15
        let projmatinf = mat4.create();

        let range = Math.tan(this.fieldOfView / 2.0) * this.getNearValue();
        let left = -range * this.aspectRatio;
        let right = range * this.aspectRatio;
        let bottom = -range;
        let top = range;

        let ep = 0.000000059604644775390625;

        projmatinf[0] = (2.0 * this.znear) / (right - left);
        projmatinf[8] = (right + left) / (right - left);
        projmatinf[5] = (2.0 * this.znear) / (top - bottom);
        projmatinf[9] = (top + bottom) / (top - bottom);
        projmatinf[10] = ep - 1.0;
        projmatinf[11] = -1.0;
        projmatinf[14] = (ep - 2.0) * this.znear;

        return projmatinf;
    }

    // http://www.orthostereo.com/geometryopengl.html
    public computeProjectionMatrix() {
        let IOD = this._sceneAPI.viewControlsModule.getInterOcularDistance() / 1000;
        let projmat = mat4.create();

        let top = Math.tan(this.DTR * this.getFieldOfView() / 2.0) * this.getNearValue(); // sets top of frustum based on fovy and near clipping plane
        let right = this.aspectRatio * top;                       // sets right of frustum based on aspect ratio

        let frustumShift = -(this.getNearValue() / 20); // 20 is focus (see threejs StereoCamera)

        this.leftFrustum.top = top;
        this.leftFrustum.bottom = -top;
        this.leftFrustum.left = -right - frustumShift;
        this.leftFrustum.right = right - frustumShift;
        this.leftFrustum.modeltranslation = (IOD / 2.0);

        this.rightFrustum.top = top;
        this.rightFrustum.bottom = -top;
        this.rightFrustum.left = -right + frustumShift;
        this.rightFrustum.right = right + frustumShift;
        this.rightFrustum.modeltranslation = -(IOD / 2.0);

        if (this._renderingLeftView) {
            mat4.frustum(projmat, this.leftFrustum.left, this.leftFrustum.right, this.leftFrustum.bottom, this.leftFrustum.top, this.getNearValue(), this.getFarValue());
            mat4.translate(projmat, projmat, vec3.fromValues(this.leftFrustum.modeltranslation, 0.0, 0.0));
        } else if (this._renderingRightView) {
            mat4.frustum(projmat, this.rightFrustum.left, this.rightFrustum.right, this.rightFrustum.bottom, this.rightFrustum.top, this.getNearValue(), this.getFarValue());
            mat4.translate(projmat, projmat, vec3.fromValues(this.rightFrustum.modeltranslation, 0.0, 0.0));
        }

        return projmat;
    }
}
